import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api, qs } from './client'
import type {
  ActivityEntry,
  AtlasNotification,
  AdminOverview,
  AdminUser,
  Company,
  DirectoryUser,
  Meeting,
  MeetingClash,
  MeetingDetail,
  MyWork,
  Pillar,
  Project,
  ProjectDetail,
  ProjectRole,
  ProjectStatus,
  Role,
  Task,
  TaskDetail,
  TaskPriority,
  TaskStatus,
} from './types'

/**
 * Anything that changes a task can change several views at once (My Work, the
 * board, a project's counts, the admin rollup), so mutations invalidate broadly
 * rather than trying to patch caches by hand. At this data volume it is
 * imperceptible and it removes a whole class of stale-UI bugs.
 */
function useInvalidateAll() {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    void queryClient.invalidateQueries({ queryKey: ['my-work'] })
    void queryClient.invalidateQueries({ queryKey: ['projects'] })
    void queryClient.invalidateQueries({ queryKey: ['companies'] })
    void queryClient.invalidateQueries({ queryKey: ['admin'] })
  }
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

export type TaskFilters = {
  assigneeId?: string
  companyId?: string
  projectId?: string
  status?: TaskStatus[]
  priority?: TaskPriority[]
  due?: 'overdue' | 'today' | 'week'
  q?: string
  includeDone?: boolean
}

export function useTasks(filters: TaskFilters = {}) {
  const search = qs({
    assigneeId: filters.assigneeId,
    companyId: filters.companyId,
    projectId: filters.projectId,
    status: filters.status?.length ? filters.status.join(',') : undefined,
    priority: filters.priority?.length ? filters.priority.join(',') : undefined,
    due: filters.due,
    q: filters.q,
    includeDone: filters.includeDone ? 'true' : undefined,
  })
  return useQuery({
    queryKey: ['tasks', search],
    queryFn: () => api<{ tasks: Task[] }>(`/tasks${search}`),
    select: (data) => data.tasks,
  })
}

export function useMyWork() {
  return useQuery({
    queryKey: ['my-work'],
    queryFn: () => api<MyWork>('/tasks/my'),
  })
}

export function useTask(taskId: string | null) {
  return useQuery({
    queryKey: ['tasks', 'detail', taskId],
    queryFn: () => api<TaskDetail>(`/tasks/${taskId}`),
    enabled: Boolean(taskId),
  })
}

export type CreateTaskInput = {
  title: string
  description?: string | null
  projectId?: string | null
  companyId?: string | null
  assigneeId?: string | null
  status?: TaskStatus
  priority?: TaskPriority
  dueDate?: string | null
}

export function useCreateTask() {
  const invalidate = useInvalidateAll()
  return useMutation({
    mutationFn: (input: CreateTaskInput) =>
      api<{ task: Task }>('/tasks', { method: 'POST', body: input }),
    onSuccess: invalidate,
  })
}

export function useUpdateTask() {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateAll()
  return useMutation({
    mutationFn: ({ id, ...patch }: Partial<CreateTaskInput> & { id: string; sortOrder?: number }) =>
      api<{ task: Task }>(`/tasks/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', variables.id] })
      invalidate()
    },
  })
}

export function useDeleteTask() {
  const invalidate = useInvalidateAll()
  return useMutation({
    mutationFn: (id: string) => api<{ ok: true }>(`/tasks/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  })
}

export function useAddComment(taskId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: string) =>
      api(`/tasks/${taskId}/comments`, { method: 'POST', body: { body } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] })
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })
}

export function useDeleteComment(taskId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (commentId: string) =>
      api<{ ok: true }>(`/comments/${commentId}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] }),
  })
}

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

export function useProjects(filters: { companyId?: string; includeArchived?: boolean } = {}) {
  const search = qs({
    companyId: filters.companyId,
    includeArchived: filters.includeArchived ? 'true' : undefined,
  })
  return useQuery({
    queryKey: ['projects', search],
    queryFn: () => api<{ projects: Project[] }>(`/projects${search}`),
    select: (data) => data.projects,
  })
}

export function useProject(projectId: string | undefined) {
  return useQuery({
    queryKey: ['projects', 'detail', projectId],
    queryFn: () => api<ProjectDetail>(`/projects/${projectId}`),
    enabled: Boolean(projectId),
  })
}

export type ProjectInput = {
  name: string
  companyId?: string
  status?: ProjectStatus
  description?: string | null
  leadUserId?: string | null
  startDate?: string | null
  targetDate?: string | null
}

export function useCreateProject() {
  const invalidate = useInvalidateAll()
  return useMutation({
    mutationFn: (input: ProjectInput) =>
      api<{ project: Project }>('/projects', { method: 'POST', body: input }),
    onSuccess: invalidate,
  })
}

export function useUpdateProject() {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateAll()
  return useMutation({
    mutationFn: ({ id, ...patch }: Partial<ProjectInput> & { id: string }) =>
      api<{ project: Project }>(`/projects/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: (_d, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['projects', 'detail', variables.id] })
      invalidate()
    },
  })
}

export function useProjectMembers(projectId: string) {
  const queryClient = useQueryClient()
  const invalidateDetail = () =>
    queryClient.invalidateQueries({ queryKey: ['projects', 'detail', projectId] })

  const add = useMutation({
    mutationFn: (input: { userId: string; role: ProjectRole }) =>
      api(`/projects/${projectId}/members`, { method: 'POST', body: input }),
    onSuccess: invalidateDetail,
  })
  const update = useMutation({
    mutationFn: (input: { userId: string; role: ProjectRole }) =>
      api(`/projects/${projectId}/members/${input.userId}`, {
        method: 'PATCH',
        body: { role: input.role },
      }),
    onSuccess: invalidateDetail,
  })
  const remove = useMutation({
    mutationFn: (userId: string) =>
      api(`/projects/${projectId}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: invalidateDetail,
  })

  return { add, update, remove }
}

// ---------------------------------------------------------------------------
// Companies
// ---------------------------------------------------------------------------

export function useCompanies() {
  return useQuery({
    queryKey: ['companies'],
    queryFn: () => api<{ companies: Company[] }>('/companies'),
    select: (data) => data.companies,
  })
}

export type CompanyInput = {
  name: string
  shortName?: string
  pillar?: Pillar
  notes?: string | null
  isActive?: boolean
}

export function useCreateCompany() {
  const invalidate = useInvalidateAll()
  return useMutation({
    mutationFn: (input: CompanyInput) =>
      api<{ company: Company }>('/companies', { method: 'POST', body: input }),
    onSuccess: invalidate,
  })
}

export function useUpdateCompany() {
  const invalidate = useInvalidateAll()
  return useMutation({
    mutationFn: ({ id, ...patch }: Partial<CompanyInput> & { id: string }) =>
      api<{ company: Company }>(`/companies/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: invalidate,
  })
}

export function useCompanyMembers() {
  const invalidate = useInvalidateAll()
  const add = useMutation({
    mutationFn: (input: {
      companyId: string
      userId: string
      title?: string
      canSchedule?: boolean
    }) =>
      api(`/companies/${input.companyId}/members`, {
        method: 'POST',
        body: { userId: input.userId, title: input.title, canSchedule: input.canSchedule },
      }),
    onSuccess: invalidate,
  })
  /**
   * Title, the scheduling right, or both. The server rejects a body carrying
   * neither rather than saving nothing, so send only what actually moved.
   */
  const update = useMutation({
    mutationFn: ({
      companyId,
      userId,
      ...patch
    }: {
      companyId: string
      userId: string
      title?: string | null
      canSchedule?: boolean
    }) => api(`/companies/${companyId}/members/${userId}`, { method: 'PATCH', body: patch }),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: (input: { companyId: string; userId: string }) =>
      api(`/companies/${input.companyId}/members/${input.userId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  })
  return { add, update, remove }
}

// ---------------------------------------------------------------------------
// Meetings
// ---------------------------------------------------------------------------

/**
 * Booking, editing or cancelling changes the calendar, the list and the day
 * strip at once, and every one of them writes an activity row, so meetings
 * mutations invalidate their own tree plus the admin rollup. They deliberately
 * do not touch tasks or projects: nothing about a meeting changes those.
 */
function useInvalidateMeetings() {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['meetings'] })
    void queryClient.invalidateQueries({ queryKey: ['admin'] })
  }
}

export type MeetingFilters = {
  /** Both required and both ISO. The API refuses an unbounded range. */
  from: string
  to: string
  companyId?: string
  /** A user id, or the literal 'me', the same way tasks accept an assignee. */
  userId?: string
  includeCancelled?: boolean
}

export function useMeetings(filters: MeetingFilters) {
  const search = qs({
    from: filters.from,
    to: filters.to,
    companyId: filters.companyId,
    userId: filters.userId,
    includeCancelled: filters.includeCancelled ? 'true' : undefined,
  })
  return useQuery({
    queryKey: ['meetings', search],
    queryFn: () => api<{ meetings: Meeting[] }>(`/meetings${search}`),
    select: (data) => data.meetings,
  })
}

export function useMeeting(meetingId: string | null) {
  return useQuery({
    queryKey: ['meetings', 'detail', meetingId],
    queryFn: () => api<MeetingDetail>(`/meetings/${meetingId}`),
    enabled: Boolean(meetingId),
  })
}

export type MeetingInput = {
  title: string
  companyId: string
  startsAt: string
  endsAt: string
  attendeeIds: string[]
  location?: string | null
  notes?: string | null
  /**
   * Set only when the booker has read a clash and chosen to go ahead anyway.
   * At least 10 characters, and it is written to the activity log verbatim.
   */
  overrideReason?: string
}

export type ClashCheckInput = {
  companyId: string
  startsAt: string
  endsAt: string
  attendeeIds: string[]
  /** Set when editing, so the meeting does not report a clash with itself. */
  excludeMeetingId?: string
}

/**
 * The dry run behind the live warning in the form. It is a mutation because it
 * posts, and because the form drives it rather than the cache: call it debounced
 * as the times and the attendees change. Treat the answer as advisory. The save
 * re-checks server side inside the write, which is what actually blocks.
 */
export function useCheckClashes() {
  return useMutation({
    mutationFn: (input: ClashCheckInput) =>
      api<{ clashes: MeetingClash[] }>('/meetings/check', { method: 'POST', body: input }),
  })
}

/**
 * A 409 from create or update carries the clashes that blocked the save, which
 * is what the "book anyway" prompt is built from. Every other failure is a
 * plain error and is left to ErrorNotice.
 */
export function clashesFromError(error: unknown): MeetingClash[] | null {
  if (!(error instanceof ApiError) || error.code !== 'MEETING_CLASH') return null
  const clashes = error.body?.clashes
  return Array.isArray(clashes) ? (clashes as MeetingClash[]) : []
}

export function useCreateMeeting() {
  const invalidate = useInvalidateMeetings()
  return useMutation({
    mutationFn: (input: MeetingInput) =>
      api<{ meeting: Meeting }>('/meetings', { method: 'POST', body: input }),
    onSuccess: invalidate,
  })
}

export function useUpdateMeeting() {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateMeetings()
  return useMutation({
    mutationFn: ({ id, ...patch }: Partial<MeetingInput> & { id: string }) =>
      api<{ meeting: Meeting }>(`/meetings/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['meetings', 'detail', variables.id] })
      invalidate()
    },
  })
}

/** Meetings are cancelled, never deleted, so the record of what was booked survives. */
export function useCancelMeeting() {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateMeetings()
  return useMutation({
    mutationFn: (id: string) =>
      api<{ meeting: Meeting }>(`/meetings/${id}/cancel`, { method: 'POST' }),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: ['meetings', 'detail', id] })
      invalidate()
    },
  })
}

/**
 * Who may book for a company. Admins may book anywhere; everybody else needs
 * `canSchedule` on their own membership. The flag only ever arrives on
 * /api/companies, so this reads it off the company rather than off the user,
 * and both the page and the drawer answer the question the same way. The server
 * re-checks on every write, so this is only ever about hiding a dead control.
 */
export function canScheduleFor(company: Company, user: { id: string; role: Role }): boolean {
  if (user.role === 'ADMIN') return true
  return company.members.some((m) => m.id === user.id && m.canSchedule)
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

export function useDirectory() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => api<{ users: DirectoryUser[] }>('/users'),
    select: (data) => data.users,
  })
}

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api<{ users: AdminUser[] }>('/admin/users'),
    select: (data) => data.users,
  })
}

export function useAdminOverview() {
  return useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => api<AdminOverview>('/admin/overview'),
  })
}

export function useAdminActivity(limit = 40) {
  return useQuery({
    queryKey: ['admin', 'activity', limit],
    queryFn: () => api<{ activity: ActivityEntry[] }>(`/admin/activity${qs({ limit })}`),
    select: (data) => data.activity,
  })
}

export function useCreateUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      email: string
      name: string
      role: 'ADMIN' | 'MEMBER'
      companies?: { companyId: string; title?: string }[]
    }) =>
      api<{ user: { id: string; email: string; name: string }; tempPassword: string }>(
        '/admin/users',
        { method: 'POST', body: input },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin'] })
      void queryClient.invalidateQueries({ queryKey: ['users'] })
      void queryClient.invalidateQueries({ queryKey: ['companies'] })
    },
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...patch
    }: {
      id: string
      name?: string
      email?: string
      role?: 'ADMIN' | 'MEMBER'
      isActive?: boolean
    }) => api(`/admin/users/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin'] })
      void queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useResetPassword() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) =>
      api<{ tempPassword: string; sessionsRevoked: number }>(
        `/admin/users/${userId}/reset-password`,
        { method: 'POST' },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  })
}

export function useRevokeSessions() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) =>
      api<{ sessionsRevoked: number }>(`/admin/users/${userId}/revoke-sessions`, {
        method: 'POST',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  })
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

/**
 * How often the badge asks whether anything new has landed.
 *
 * A minute, by polling, deliberately. Ten people at one request a minute is
 * nothing, and the alternative, a websocket or an event stream, is the first
 * thing to break behind a proxy and the hardest thing to debug when it does.
 * Switching tabs back also refetches, because `refetchOnWindowFocus` is on
 * globally, so in practice it is usually faster than a minute.
 */
const BELL_POLL_MS = 60_000

export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api<{ count: number }>('/notifications/unread-count'),
    select: (data) => data.count,
    refetchInterval: BELL_POLL_MS,
  })
}

export function useNotifications(limit = 20) {
  return useQuery({
    queryKey: ['notifications', 'list', limit],
    queryFn: () =>
      api<{ notifications: AtlasNotification[] }>(`/notifications${qs({ limit })}`),
    select: (data) => data.notifications,
  })
}

/** Marks specific notifications read, or all of them with `{ all: true }`. */
export function useMarkNotificationsRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { ids?: string[]; all?: boolean }) =>
      api<{ marked: number }>('/notifications/read', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}
